import { auth, db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  updateDoc,
  where,
} from "firebase/firestore";
import { lichSuKhoApi } from "./lichSuKho";
import { langNgheFirestore } from "../lib/langNgheFirestore";
import { chuanHoaMaTimKiem } from "../lib/chuanHoaMaTimKiem";
import { giaoDichApi } from "./giaoDich";

const sotCol = () => collection(db, "sot");

// Đặt chỗ Mã Sọt — document ID = mã in trên tem (vd. LV1HS6). Create-only
// (xem firestore.rules) nên 2 admin tạo cùng lúc không cấp trùng mã.
const maSotDaCapDoc = (ma) => doc(db, "maSotDaCap", ma);

const TIEN_TO_MA_SOT = "LV";
const SO_KY_TU_RANDOM = 4;
const BANG_KY_TU_MA_SOT = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const SO_LAN_THU_MA_SOT = 8;

// Khối lượng bì là thông tin tham khảo, KHÔNG bắt buộc nhập — form gửi lên
// chuỗi rỗng khi người dùng bỏ trống, Number("") lại ra 0 nên phải quy về null
// tường minh để phân biệt "không nhập" với "bì nặng 0kg".
function soHoacNull(giaTri) {
  if (giaTri === null || giaTri === undefined || giaTri === "") return null;
  const so = Number(giaTri);
  return Number.isNaN(so) ? null : so;
}

function sinhKyTuRandom(soKyTu) {
  const n = BANG_KY_TU_MA_SOT.length;
  const bytes = new Uint8Array(soKyTu);
  crypto.getRandomValues(bytes);
  let ketQua = "";
  for (let i = 0; i < soKyTu; i += 1) {
    ketQua += BANG_KY_TU_MA_SOT[bytes[i] % n];
  }
  return ketQua;
}

// Mã Sọt mới = mã chữ in dưới QR (dự phòng khi máy quét hỏng): LV + 4 ký tự
// random (0-9, A-Z), ví dụ LV1HS6. Sọt đã in tem cũ (PL-… / ST-…) giữ nguyên
// qrCode — tem ngoài thực địa vẫn quét được.
function ghepMaSot() {
  return `${TIEN_TO_MA_SOT}${sinhKyTuRandom(SO_KY_TU_RANDOM)}`;
}

class TrungMaSotError extends Error {
  constructor(ma) {
    super(`Mã Sọt ${ma} đã được cấp`);
    this.name = "TrungMaSotError";
  }
}

function mapDocSot(d) {
  return { id: d.id, ...d.data() };
}

/** Exact qrCode/maSot trước; nếu thiếu gạch / lệch hoa thường thì chuẩn hoá quét. */
async function timSotTheoMaLinhHoat(maNhap) {
  const raw = String(maNhap ?? "").trim();
  if (!raw) return null;

  const bienThe = [...new Set([raw, raw.toUpperCase(), raw.toLowerCase()])];
  const snaps = await Promise.all(
    bienThe.flatMap((v) => [
      getDocs(query(sotCol(), where("qrCode", "==", v))),
      getDocs(query(sotCol(), where("maSot", "==", v))),
    ])
  );
  for (const snap of snaps) {
    if (!snap.empty) return mapDocSot(snap.docs[0]);
  }

  const chuan = chuanHoaMaTimKiem(raw);
  if (!chuan) return null;

  const all = await getDocs(sotCol());
  const hit = all.docs.find((d) => {
    const data = d.data();
    return chuanHoaMaTimKiem(data.qrCode) === chuan || chuanHoaMaTimKiem(data.maSot) === chuan;
  });
  return hit ? mapDocSot(hit) : null;
}

export const sotApi = {
  async theoQr(qrCode) {
    const sot = await timSotTheoMaLinhHoat(qrCode);
    if (!sot) throw new Error("Không tìm thấy Sọt với mã QR này");
    return sot;
  },

  async danhSach(trangThai) {
    const q = trangThai ? query(sotCol(), where("trangThai", "==", trangThai)) : sotCol();
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  langNgheDanhSach(trangThai, onChange, onError) {
    const target = trangThai ? query(sotCol(), where("trangThai", "==", trangThai)) : sotCol();
    return langNgheFirestore(target, { onChange, onError });
  },

  async chiTiet(id) {
    const snap = await getDoc(doc(db, "sot", id));
    if (!snap.exists()) throw new Error("Không tìm thấy Sọt");
    return { id: snap.id, ...snap.data() };
  },

  langNgheChiTiet(id, onChange, onError) {
    if (!id) {
      onChange(null);
      return () => {};
    }
    return langNgheFirestore(doc(db, "sot", id), { onChange, onError });
  },

  async taoMoi({ khoiLuongToiDa, khoiLuongBi, ghiChu }) {
    // Đặt chỗ maSotDaCap/{ma} + tạo Sọt trong CÙNG 1 transaction — giống
    // hopTacXaApi.taoMoi: không có trạng thái nửa vời và 2 admin tạo cùng
    // lúc không trùng mã. Random 4 ký tự có thể trùng (rất hiếm) → thử lại.
    let lanThuCuoi = null;
    for (let lan = 0; lan < SO_LAN_THU_MA_SOT; lan += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await runTransaction(db, async (tx) => {
          const maSot = ghepMaSot();
          const refMa = maSotDaCapDoc(maSot);
          const snapMa = await tx.get(refMa);
          if (snapMa.exists()) throw new TrungMaSotError(maSot);

          const createdAt = new Date().toISOString();
          const data = {
            maSot,
            qrCode: maSot,
            khoiLuongToiDa: Number(khoiLuongToiDa),
            // Chỉ lưu tham khảo (khối lượng vỏ Sọt) — không tham gia ngưỡng
            // đóng Sọt trong tinhTrangThaiTiepTheo, cũng không cộng vào
            // khoiLuongHienTai.
            khoiLuongBi: soHoacNull(khoiLuongBi),
            khoiLuongHienTai: 0,
            soGiaoDich: 0,
            trangThai: "moi_tao",
            nguoiTao: auth.currentUser?.uid ?? null,
            ghiChu: ghiChu ?? null,
            createdAt,
          };

          const refSot = doc(sotCol());
          tx.set(refMa, { sotId: refSot.id, createdAt });
          tx.set(refSot, data);
          return { id: refSot.id, ...data };
        });
      } catch (err) {
        if (!(err instanceof TrungMaSotError)) throw err;
        lanThuCuoi = err;
      }
    }
    throw lanThuCuoi ?? new Error("Không cấp được mã Sọt duy nhất, thử lại.");
  },

  // Nhân viên kho quét Sọt khi chở về kho — không bắt buộc phải đầy mới
  // nhập kho được (chở về ít cũng nhập), chỉ chặn nếu đã nhập kho rồi,
  // chưa có giao dịch thu mua, hoặc còn phiếu chưa được kế toán thanh toán.
  // Atomic: đọc Sọt + validate trạng thái + cập nhật Sọt + ghi lichSuKho
  // trong 1 transaction — chặn race 2 người quét cùng lúc, và không để Sọt
  // đã chuyển trạng thái mà thiếu dòng lịch sử nếu ghi log lỗi giữa chừng.
  async nhapKho(id, { khoiLuongCanLai }) {
    const canLai = Number(khoiLuongCanLai);
    if (!Number.isFinite(canLai) || canLai <= 0) {
      throw new Error("Khối lượng cân lại phải lớn hơn 0.");
    }
    const snapTruoc = await getDoc(doc(db, "sot", id));
    if (!snapTruoc.exists()) throw new Error("Không tìm thấy Sọt");
    const sotTruoc = snapTruoc.data();
    if (!sotTruoc.chuKyHienTai) {
      throw new Error(
        "Sọt thiếu mã chu kỳ — không nhập kho được. Phiếu thu mua phải gắn chu kỳ trước."
      );
    }
    const chuKyLucDoc = sotTruoc.chuKyHienTai;
    const { poIds, soPhieu } = await giaoDichApi.kiemTraThanhToanTruocNhapKho(id, chuKyLucDoc);

    return runTransaction(db, async (tx) => {
      const refSot = doc(db, "sot", id);
      const snap = await tx.get(refSot);
      if (!snap.exists()) throw new Error("Không tìm thấy Sọt");
      const sot = snap.data();

      if (sot.trangThai === "da_nhap_kho") {
        throw new Error("Sọt này đã nhập kho rồi.");
      }
      if (!sot.khoiLuongHienTai) {
        throw new Error("Sọt này chưa có giao dịch thu mua nào, chưa thể nhập kho.");
      }
      if (!sot.chuKyHienTai) {
        throw new Error(
          "Sọt thiếu mã chu kỳ — không nhập kho được. Phiếu thu mua phải gắn chu kỳ trước."
        );
      }
      const thuMua = Number(sot.khoiLuongHienTai);
      if (canLai > thuMua) {
        throw new Error(
          `Khối lượng nhập kho (${canLai} kg) không được lớn hơn số thu mua trên Sọt (${thuMua} kg).`
        );
      }

      // Đọc lại từng PO trong transaction — chặn race đổi trạng thái TT / thêm phiếu.
      if ((sot.soGiaoDich ?? 0) > soPhieu) {
        throw new Error("Sọt vừa có phiếu thu mua mới — tải lại rồi nhập kho.");
      }
      for (const poId of poIds) {
        const poSnap = await tx.get(doc(db, "giaoDich", poId));
        if (!poSnap.exists()) {
          throw new Error("Phiếu thu mua gắn Sọt đã bị xoá — không thể nhập kho.");
        }
        if (poSnap.data().trangThaiThanhToan !== "da_thanh_toan") {
          throw new Error(
            "Còn phiếu chưa được kế toán thanh toán — không thể nhập kho (trạng thái vừa đổi)."
          );
        }
      }

      const chuKyId = sot.chuKyHienTai;
      tx.update(refSot, {
        trangThai: "da_nhap_kho",
        khoiLuongCanLai: canLai,
        nhapKhoLuc: new Date().toISOString(),
        nguoiNhapKho: auth.currentUser?.uid ?? null,
        chuKyHienTai: chuKyId,
      });
      await lichSuKhoApi.ghi(
        {
          loai: "nhap",
          maSot: id,
          chuKyId,
          khoiLuongThuMua: sot.khoiLuongHienTai,
          khoiLuongCanLai: canLai,
          chenhLech: canLai - sot.khoiLuongHienTai,
        },
        tx
      );
      return {
        id,
        ...sot,
        trangThai: "da_nhap_kho",
        khoiLuongCanLai: canLai,
        chuKyHienTai: chuKyId,
      };
    });
  },

  // Nhân viên kho quét Sọt đã nhập kho khi hàng đã xuất khỏi kho (bán đi)
  // — trả Sọt về rỗng để tái sử dụng. Không đụng tới các giao dịch cũ (vẫn
  // tra cứu được qua giaoDichApi.theoSot bằng đúng ID Sọt này). Bơ nằm
  // trong kho một thời gian để ủ trước khi xuất nên khối lượng thường hao hụt
  // so với lúc nhập kho — bắt buộc cân lại lần nữa lúc xuất để ghi nhận đúng
  // số kg thực xuất, không lấy lại số cũ. Không cho xuất lớn hơn số lúc nhập.
  async xuatKho(id, { khoiLuongXuat }) {
    const xuat = Number(khoiLuongXuat);
    if (!Number.isFinite(xuat) || xuat <= 0) {
      throw new Error("Khối lượng xuất phải lớn hơn 0.");
    }
    return runTransaction(db, async (tx) => {
      const refSot = doc(db, "sot", id);
      const snap = await tx.get(refSot);
      if (!snap.exists()) throw new Error("Không tìm thấy Sọt");
      const sot = snap.data();

      if (sot.trangThai !== "da_nhap_kho") {
        throw new Error("Sọt chưa nhập kho, chưa thể xuất kho.");
      }

      const tranTran = sot.khoiLuongCanLai != null ? Number(sot.khoiLuongCanLai) : null;
      if (tranTran == null || !Number.isFinite(tranTran) || tranTran <= 0) {
        throw new Error("Sọt chưa có khối lượng cân lại lúc nhập kho — không thể xuất.");
      }
      if (xuat > tranTran) {
        throw new Error(
          `Khối lượng xuất (${xuat} kg) không được lớn hơn số đã nhập kho (${tranTran} kg).`
        );
      }

      // Xuất kho là chốt lại 1 chu kỳ hoàn chỉnh (thu mua -> nhập kho -> xuất
      // kho) — dùng lại đúng mã chu kỳ để nối với dòng "nhap" tương ứng, rồi
      // xoá hẳn mã khỏi Sọt; chu kỳ tiếp theo (lượt thu mua kế tiếp) sẽ tự
      // sinh mã khác.
      const chuKyId = sot.chuKyHienTai ?? null;
      tx.update(refSot, {
        khoiLuongHienTai: 0,
        soGiaoDich: 0,
        khoiLuongCanLai: null,
        nhapKhoLuc: null,
        nguoiNhapKho: null,
        chuKyHienTai: null,
        trangThai: "moi_tao",
      });
      await lichSuKhoApi.ghi(
        {
          loai: "xuat",
          maSot: id,
          chuKyId,
          khoiLuongThuMua: sot.khoiLuongHienTai,
          khoiLuongCanLai: sot.khoiLuongCanLai ?? null,
          khoiLuongXuat: xuat,
          chenhLech: sot.khoiLuongCanLai != null ? xuat - sot.khoiLuongCanLai : null,
        },
        tx
      );
      return {
        id,
        ...sot,
        khoiLuongHienTai: 0,
        soGiaoDich: 0,
        trangThai: "moi_tao",
        chuKyHienTai: null,
      };
    });
  },

  async capNhat(id, { khoiLuongToiDa, khoiLuongBi, ghiChu }) {
    await updateDoc(doc(db, "sot", id), {
      khoiLuongToiDa: Number(khoiLuongToiDa),
      khoiLuongBi: soHoacNull(khoiLuongBi),
      ghiChu: ghiChu ?? null,
    });
    return sotApi.chiTiet(id);
  },
};
